/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const API = 'https://coast-terminal-production.up.railway.app';

// ── Tokens (matches Coast Deal Finder palette) ─────────────────────────────
const bg        = '#ffffff';
const surface   = '#fafaf8';
const surfaceHi = '#f5f0e8';
const border    = '#d4c8b4';
const gold      = '#b8956a';
const goldLt    = '#d4c8b4';
const text      = '#0d3d5c';
const textSec   = '#12526e';
const textMut   = '#4a6070';
const green     = '#1A7A4A';
const red       = '#CC2200';
const blue      = '#1A5A8A';
const fHead = "'Fraunces', Georgia, serif";
const fBody = "'EB Garamond', Georgia, serif";

// ── Types ──────────────────────────────────────────────────────────────────
interface Action {
  id: number;
  cik: string;
  firm_name: string;
  title: string;
  url: string | null;
  source: string | null;
  published_at: string | null;
  summary: string | null;
  relevance: string | null;
  deal_size_usd: number | null;
  property_type: string | null;
  market: string | null;
  importance_score: number | null;
  firm_focus: string | null;
  analysis: AnalysisRich | null;
}

interface AnalysisRich {
  headline?:          string | null;
  why?:               string | null;
  thesis_fit?:        string | null;
  capital_signal?:    string | null;
  market_signal?:     string | null;
  comparable_deals?:  string | null;
  risk_factors?:      string | null;
  investor_takeaway?: string | null;
  what_to_watch?:     string | null;
  prediction?:        string | null;
  impact?:            string | null;
  key_metrics?: {
    estimated_cap_rate?: string | null;
    estimated_leverage?: string | null;
    estimated_hold?:     string | null;
    deal_structure?:     string | null;
  } | null;
}

interface Stats {
  deals_7d: number;  raises_7d: number;
  deals_30d: number; raises_30d: number;
  deals_90d: number; raises_90d: number;
  deals_all: number; raises_all: number; news_all: number;
  volume_30d: number;
  firms_active: number;
}

interface FeedResponse {
  total: number;
  offset: number;
  limit: number;
  actions: Action[];
  stats: Stats;
  top_sectors: { sector: string; n: number }[];
}

interface Props { token: string; }

// ── Helpers ────────────────────────────────────────────────────────────────
function fmt$(n: number | null | undefined): string {
  if (!n) return '—';
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(0)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
}

function fmtDate(s: string | null): string {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - d.getTime()) / 86400000);
  if (diffDays === 0)  return 'Today';
  if (diffDays === 1)  return 'Yesterday';
  if (diffDays <= 7)   return `${diffDays}d ago`;
  if (diffDays <= 30)  return `${Math.floor(diffDays / 7)}w ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function actionVerb(rel: string | null, title: string): { verb: string; color: string } {
  const t = (title || '').toLowerCase();
  if (rel === 'fundraise')       return { verb: 'RAISED',     color: gold };
  if (rel === 'deal') {
    if (t.includes('sold') || t.includes('disposes') || t.includes('disposition'))
                                  return { verb: 'SOLD',       color: red };
    if (t.includes('joint venture') || t.includes('jv ') || t.includes(' jv'))
                                  return { verb: 'JV',         color: blue };
    if (t.includes('refinanc') || t.includes('senior loan') || t.includes('debt'))
                                  return { verb: 'FINANCED',   color: blue };
    if (t.includes('hires') || t.includes('appointed') || t.includes('joins'))
                                  return { verb: 'HIRED',      color: textMut };
    if (t.includes('launches') || t.includes('launched') || t.includes('debuts'))
                                  return { verb: 'LAUNCHED',   color: gold };
    return                          { verb: 'ACQUIRED',   color: green };
  }
  return { verb: 'ANNOUNCED', color: textMut };
}

// ── Stats strip ────────────────────────────────────────────────────────────
function StatsStrip({ stats, totalShown, totalMatching }: {
  stats: Stats;
  totalShown: number;
  totalMatching: number;
}) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
      gap: 1,
      background: border,
      border: `1px solid ${border}`,
      marginBottom: 24,
    }}>
      <Stat label="Tracked Actions" value={totalMatching.toLocaleString()} sub={`${totalShown} loaded`} />
      <Stat label="Deals · Last 7d"  value={stats.deals_7d.toString()}  sub={`${stats.deals_30d} in 30d`} />
      <Stat label="Raises · Last 7d" value={stats.raises_7d.toString()} sub={`${stats.raises_30d} in 30d`} />
      <Stat label="Volume · 30d"     value={fmt$(stats.volume_30d)}     sub="reported deal sizes" />
      <Stat label="Active Firms"     value={stats.firms_active.toString()} sub="with coverage" />
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div style={{ background: '#ffffff', padding: '14px 18px' }}>
      <div style={{
        color: textMut, fontFamily: fBody, fontSize: 9,
        letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700,
        marginBottom: 4,
      }}>
        {label}
      </div>
      <div style={{
        color: text, fontFamily: fHead, fontSize: 22, fontWeight: 800,
        lineHeight: 1.1,
      }}>
        {value}
      </div>
      {sub && (
        <div style={{ color: textMut, fontFamily: fBody, fontSize: 11, marginTop: 4 }}>
          {sub}
        </div>
      )}
    </div>
  );
}

// ── Action Row ─────────────────────────────────────────────────────────────
function ActionRow({ action }: { action: Action }) {
  const [expanded, setExpanded] = useState(false);
  const { verb, color } = actionVerb(action.relevance, action.title);

  return (
    <div style={{
      borderBottom: `1px solid ${border}`,
      background: expanded ? surfaceHi : 'transparent',
      transition: 'background 0.12s',
    }}>
      <div
        onClick={() => setExpanded(e => !e)}
        style={{
          display: 'grid',
          gridTemplateColumns: '88px 80px 1fr auto',
          gap: 16,
          alignItems: 'baseline',
          padding: '12px 16px',
          cursor: 'pointer',
        }}
      >
        {/* Date */}
        <div style={{
          color: textMut, fontFamily: fBody, fontSize: 11,
          letterSpacing: '0.04em', whiteSpace: 'nowrap',
        }}>
          {fmtDate(action.published_at)}
        </div>

        {/* Action type ribbon */}
        <div style={{
          color, fontFamily: fHead, fontSize: 11, fontWeight: 800,
          letterSpacing: '0.14em', textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}>
          {verb}
        </div>

        {/* Headline cluster */}
        <div style={{ minWidth: 0 }}>
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'baseline',
          }}>
            <span style={{
              color: text, fontFamily: fHead, fontSize: 15, fontWeight: 700,
              letterSpacing: '-0.005em',
            }}>
              {action.firm_name}
            </span>
            <span style={{
              color: textSec, fontFamily: fBody, fontSize: 14,
              flex: 1, minWidth: 0,
            }}>
              {action.title}
            </span>
          </div>
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 4,
            color: textMut, fontFamily: fBody, fontSize: 11,
          }}>
            {action.deal_size_usd && (
              <span style={{ color: green, fontWeight: 700 }}>{fmt$(action.deal_size_usd)}</span>
            )}
            {action.property_type && (
              <span style={{ textTransform: 'capitalize' }}>{action.property_type}</span>
            )}
            {action.market && <span>{action.market}</span>}
            {action.source && (
              <span style={{
                color: gold, letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: 10,
              }}>
                {action.source}
              </span>
            )}
          </div>
        </div>

        {/* Right: chevron only — AI breakdowns removed for factual-only rule */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', whiteSpace: 'nowrap' }}>
          <span style={{
            color: textMut, fontFamily: fBody, fontSize: 12,
            transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.15s', display: 'inline-block', width: 12,
          }}>
            ▶
          </span>
        </div>
      </div>

      {/* Expanded panel — primary-source view only (no AI editorial) */}
      {expanded && (
        <div style={{ padding: '0 16px 18px 184px', maxWidth: 1100 }}>
          {action.summary && (
            <p style={{
              fontFamily: fBody, color: textSec, fontSize: 13,
              lineHeight: 1.65, margin: '6px 0 14px 0',
            }}>
              {action.summary}
            </p>
          )}

          {action.url && (
            <a href={action.url} target="_blank" rel="noopener noreferrer"
              style={{
                color: gold, fontFamily: fBody, fontSize: 12,
                textDecoration: 'none', borderBottom: `1px solid ${gold}55`,
                paddingBottom: 1,
              }}>
              View original source →
            </a>
          )}
        </div>
      )}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ background: '#fff', padding: '8px 12px' }}>
      <div style={{
        color: textMut, fontFamily: fBody, fontSize: 9,
        letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700,
        marginBottom: 3,
      }}>
        {label}
      </div>
      <div style={{
        color: text, fontFamily: fHead, fontSize: 14, fontWeight: 800,
        textTransform: 'capitalize',
      }}>
        {value}
      </div>
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{
        color: textMut, fontFamily: fBody, fontSize: 9,
        letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 4,
      }}>
        {label}
      </div>
      <p style={{
        fontFamily: fBody, color: text, fontSize: 13,
        lineHeight: 1.65, margin: 0,
      }}>
        {children}
      </p>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────
export default function Peers({ token }: Props) {
  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [data, setData]       = useState<FeedResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  // Filters
  const [actionType, setActionType] = useState<'all' | 'deal' | 'fundraise' | 'news'>('all');
  const [days,       setDays]       = useState<number | null>(null);  // null = all-time
  const [sector,     setSector]     = useState<string>('');
  const [q,          setQ]          = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [sort,       setSort]       = useState<'newest' | 'size_desc' | 'firm'>('newest');
  const [focusApplied, setFocusApplied] = useState(false);

  // Apply user focus once on mount: if a focus is set, default the sector
  // filter to the focused property type. The user can clear it from the
  // sector-heat strip just like any other filter.
  useEffect(() => {
    if (focusApplied) return;
    axios.get(`${API}/me/focus`, { headers: auth })
      .then(r => {
        const f = r.data?.focus;
        if (f && f.property_type) setSector(f.property_type.toLowerCase());
      })
      .catch(() => {})
      .finally(() => setFocusApplied(true));
  }, [auth, focusApplied]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = { limit: 100, sort };
      if (actionType !== 'all') params.action = actionType;
      if (days)                 params.days   = days;
      if (sector)               params.sector = sector;
      if (debouncedQ)           params.q      = debouncedQ;
      const r = await axios.get(`${API}/peers/actions`, { headers: auth, params });
      setData(r.data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load actions');
    } finally {
      setLoading(false);
    }
  }, [auth, actionType, days, sector, debouncedQ, sort]);

  useEffect(() => { load(); }, [load]);

  const stats = data?.stats || {
    deals_7d: 0, raises_7d: 0, deals_30d: 0, raises_30d: 0,
    deals_90d: 0, raises_90d: 0, deals_all: 0, raises_all: 0, news_all: 0,
    volume_30d: 0, firms_active: 0,
  };

  return (
    <div style={{ background: bg, minHeight: '100%' }}>
      {/* Header */}
      <div style={{
        padding: '32px 44px 18px',
        borderBottom: `1px solid ${border}`,
        background: bg,
      }}>
        <div style={{
          color: gold, fontSize: 10, fontFamily: fBody,
          letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700,
          marginBottom: 6,
        }}>
          Coast · Peer Intelligence
        </div>
        <h1 style={{
          fontFamily: fHead, color: text, fontSize: 38, fontWeight: 900,
          margin: 0, letterSpacing: '-0.01em', lineHeight: 1.1,
        }}>
          Action Tracker
        </h1>
        <div style={{
          color: textMut, fontFamily: fBody, fontSize: 14,
          marginTop: 8, maxWidth: 720,
        }}>
          Every acquisition, disposition, fundraise, hire, and strategic move from
          {' '}{stats.firms_active} tracked institutional real estate firms — sourced from
          industry trades and refreshed daily. Click any row for the AI-written
          why / impact / outlook breakdown.
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '24px 44px 60px' }}>
        <StatsStrip
          stats={stats}
          totalShown={data?.actions.length ?? 0}
          totalMatching={data?.total ?? 0}
        />

        {/* Filter bar */}
        <div style={{
          display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center',
          marginBottom: 18, paddingBottom: 16, borderBottom: `1px solid ${border}`,
        }}>
          {/* Action type pills */}
          {(['all', 'deal', 'fundraise', 'news'] as const).map(t => {
            const active = actionType === t;
            const counts: Record<string, number> = {
              all: (stats.deals_all + stats.raises_all + stats.news_all),
              deal: stats.deals_all, fundraise: stats.raises_all, news: stats.news_all,
            };
            return (
              <button key={t} onClick={() => setActionType(t)}
                style={{
                  background: active ? gold : 'transparent',
                  color: active ? '#fff' : textSec,
                  border: `1px solid ${active ? gold : border}`,
                  padding: '5px 12px',
                  fontFamily: fBody, fontSize: 11, fontWeight: 600,
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                  cursor: 'pointer',
                }}>
                {t === 'all' ? 'All' : t === 'deal' ? 'Deals' : t === 'fundraise' ? 'Raises' : 'News'} ({counts[t]})
              </button>
            );
          })}

          <div style={{ width: 1, height: 24, background: border }} />

          {/* Time range */}
          {([
            ['7d',  7],
            ['30d', 30],
            ['90d', 90],
            ['All', null],
          ] as [string, number | null][]).map(([label, d]) => {
            const active = days === d;
            return (
              <button key={label} onClick={() => setDays(d)}
                style={{
                  background: active ? text : 'transparent',
                  color: active ? '#fff' : textMut,
                  border: `1px solid ${active ? text : border}`,
                  padding: '5px 11px',
                  fontFamily: fBody, fontSize: 11, fontWeight: 600,
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                  cursor: 'pointer',
                }}>
                {label}
              </button>
            );
          })}

          <div style={{ width: 1, height: 24, background: border }} />

          {/* Sort */}
          <select value={sort} onChange={e => setSort(e.target.value as any)}
            style={{
              background: '#fff', color: text,
              border: `1px solid ${border}`,
              padding: '5px 10px',
              fontFamily: fBody, fontSize: 11,
              cursor: 'pointer',
            }}>
            <option value="newest">Newest first</option>
            <option value="size_desc">Largest deal</option>
            <option value="firm">Firm A→Z</option>
          </select>

          {/* Search */}
          <input
            placeholder="Search firm or headline…"
            value={q} onChange={e => setQ(e.target.value)}
            style={{
              flex: 1, minWidth: 180,
              background: '#fff', color: text,
              border: `1px solid ${border}`,
              padding: '6px 12px',
              fontFamily: fBody, fontSize: 13,
              outline: 'none',
            }}
          />
        </div>

        {/* Top sectors hot strip */}
        {data && data.top_sectors.length > 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
            marginBottom: 20,
            color: textMut, fontFamily: fBody, fontSize: 12,
          }}>
            <span style={{
              color: gold, fontSize: 9, fontWeight: 700,
              letterSpacing: '0.16em', textTransform: 'uppercase',
            }}>
              30d sector heat:
            </span>
            {data.top_sectors.map(s => (
              <button key={s.sector} onClick={() => setSector(sector === s.sector ? '' : s.sector)}
                style={{
                  background: sector === s.sector ? gold : 'transparent',
                  color: sector === s.sector ? '#fff' : textSec,
                  border: `1px solid ${sector === s.sector ? gold : border}`,
                  padding: '3px 9px',
                  fontFamily: fBody, fontSize: 11,
                  cursor: 'pointer', textTransform: 'capitalize',
                }}>
                {s.sector} <strong style={{ marginLeft: 4 }}>{s.n}</strong>
              </button>
            ))}
            {sector && (
              <button onClick={() => setSector('')}
                style={{
                  background: 'none', border: 'none', color: textMut,
                  fontFamily: fBody, fontSize: 11, cursor: 'pointer',
                  textDecoration: 'underline',
                }}>
                clear
              </button>
            )}
          </div>
        )}

        {/* Action list */}
        {loading && !data ? (
          <div style={{ color: textMut, fontFamily: fBody, fontSize: 14, padding: '40px 0', textAlign: 'center' }}>
            Loading actions…
          </div>
        ) : error ? (
          <div style={{
            background: '#fff5f0', border: '1px solid #f0c8b0',
            borderLeft: '3px solid #CC2200',
            padding: '20px 24px', color: '#7a2a10',
            fontFamily: fBody, fontSize: 13,
          }}>
            {error}
          </div>
        ) : data && data.actions.length === 0 ? (
          <div style={{
            color: textMut, fontFamily: fBody, fontSize: 14,
            padding: '40px 0', textAlign: 'center',
            border: `1px dashed ${border}`,
          }}>
            No actions match these filters. Try widening the time range or clearing search.
          </div>
        ) : (
          <div style={{ background: '#fff', border: `1px solid ${border}` }}>
            {data!.actions.map(a => <ActionRow key={a.id} action={a} />)}
          </div>
        )}

        {/* Pagination hint */}
        {data && data.total > data.actions.length && (
          <div style={{
            color: textMut, fontFamily: fBody, fontSize: 12,
            textAlign: 'center', padding: '16px 0',
          }}>
            Showing first {data.actions.length.toLocaleString()} of {data.total.toLocaleString()} matching actions.
            Narrow your filters to focus on a slice.
          </div>
        )}
      </div>
    </div>
  );
}
